import React, { useState, useEffect, useCallback } from 'react'
import './PaymentVerificationPanel.scss'
import Spinner from '../spinner'
import Axios from '../../utilities/axios'

const PaymentVerificationPanel = ({
  status = 'success',
  onClose = () => {},
  retries = 5
}) => {
  const [verificationState, setVerificationState] = useState('idle') // 'idle', 'verifying', 'success', 'timeout', 'failed'
  const [showCloseButton, setShowCloseButton] = useState(false)

  const initialDelay = 2000 // 2 seconds

  const verifyUpgrade = useCallback(async () => {
    try {
      const { data } = await Axios.get('user/self')
      const user = data?.user || data

      if (user?.subscription === 'pro' || user?.subscription === 'custom') {
        setVerificationState('success')
        setShowCloseButton(true)
        return true
      }
      return false
    } catch (error) {
      return false
    }
  }, [])

  const runVerificationWithRetries = useCallback(async () => {
    setVerificationState('verifying')

    for (let attempt = 0; attempt < retries; attempt++) {

      const isVerified = await verifyUpgrade()
      if (isVerified) {
        return
      }

      // If not the last attempt, wait with exponential backoff
      if (attempt < retries - 1) {
        const delay = initialDelay * Math.pow(2, attempt) // 2s, 4s, 8s
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }

    // All retries exhausted without success
    setVerificationState('timeout')
    setShowCloseButton(true)
  }, [retries, verifyUpgrade])

  useEffect(() => {
    if (status === 'success') {
      runVerificationWithRetries()
    } else if (status === 'failed') {
      setVerificationState('failed')
      setShowCloseButton(true)
    }
  }, [status, runVerificationWithRetries])

  const renderStatusSteps = () => {
    if (status === 'failed') {
      return (
        <>
          <div className="pv-panel-status-row">
            <i className="fas fa-check-circle pv-panel-status-icon success"></i>
            <span className="pv-panel-status-text">Talking to Stripe</span>
          </div>
          <div className="pv-panel-status-row">
            <i className="fas fa-times-circle pv-panel-status-icon warning"></i>
            <span className="pv-panel-status-text">Payment processing failed</span>
          </div>
        </>
      )
    }

    const steps = [
      {
        key: 'redirect',
        text: 'Redirected from payment processor',
        done: true
      },
      {
        key: 'verify',
        text: 'Verifying upgrade',
        // text: verificationState === 'verifying'
        //   ? `Verifying upgrade (attempt ${currentAttempt}/${retries})`
        //   : 'Verifying upgrade',
        done: verificationState === 'success',
        loading: verificationState === 'verifying',
        warning: verificationState === 'timeout'
      },
      {
        key: 'complete',
        text: verificationState === 'success'
          ? 'Upgrade confirmed'
          : verificationState === 'timeout'
            ? 'Verification pending'
            : 'Confirming plan status',
        done: verificationState === 'success',
        loading: verificationState === 'verifying',
        warning: verificationState === 'timeout'
      }
    ]

    return steps.map((step) => (
      <div key={step.key} className="pv-panel-status-row">
        {step.done ? (
          <i className="fas fa-check-circle pv-panel-status-icon success"></i>
        ) : step.warning ? (
          <i className="fas fa-exclamation-circle pv-panel-status-icon warning"></i>
        ) : step.loading ? (
          <Spinner size={14} dark={true} />
        ) : (
          <Spinner size={14} dark={true} style={{ borderTopColor: '#323741' }} />
        )}
        <span className="pv-panel-status-text">{step.text}</span>
      </div>
    ))
  }

  const renderMessage = () => {
    if (verificationState === 'success') {
      return (
        <div className="pv-panel-message-box">
          <div className="pv-panel-message-title">
            <i className="fas fa-check-circle" style={{ color: 'rgb(29, 191, 113)' }}></i>
            Upgrade Successful
          </div>
          <p className="pv-panel-message-text">
            Your account has been upgraded to Nelima Pro Plan. You now have access to all premium features. Thank you for your support!
          </p>
        </div>
      )
    }

    if (verificationState === 'timeout') {
      return (
        <div className="pv-panel-message-box">
          <div className="pv-panel-message-title">
            <i className="fas fa-clock" style={{ opacity: 0.5 }}></i>
            Still Waiting for Verification
          </div>
          <p className="pv-panel-message-text">
            We're still waiting to confirm your payment. This can sometimes take a few minutes.
            If your upgrade doesn't appear soon, please{' '}
            <a href="/support" className="pv-panel-link">contact support</a>.
          </p>
        </div>
      )
    }

    if (verificationState === 'failed' || status === 'failed') {
      return (
        <div className="pv-panel-message-box">
          <div className="pv-panel-message-title">
            <i className="fas fa-info-circle" style={{ opacity: 0.5 }}></i>
            Payment Failed
          </div>
          <p className="pv-panel-message-text">
            Your payment could not be processed. Please try again or use a different payment method.
            <br /><br />
            <a href="/upgrade" className="pv-panel-link">Try again</a>
            {' '}or{' '}
            <a href="https://calendly.com/james-kachamila/30min" target='_blank' className="pv-panel-link">contact support</a> if you need assistance.
          </p>
        </div>
      )
    }

    return null
  }

  return (
    <div className="pv-panel-container">
      {showCloseButton && (
        <div className="pv-panel-close-button" onClick={onClose}>
          <i className="fal fa-times"></i>
        </div>
      )}

      <div className="pv-panel-title">Plan Verification</div>

      <div className="pv-panel-sub-title">
        <i style={{ fontSize: '22px', opacity: 0.5, marginRight: '12px' }} className="fal fa-credit-card"></i>
        Payment Status
      </div>

      <p className="pv-panel-description">
        {status === 'success'
          ? 'Verifying your payment and activating your upgraded plan.'
          : 'There was an issue processing your payment.'}
      </p>

      <div className="pv-panel-widget">
        <div className="pv-panel-widget-icon">
          <i className="fal fa-receipt"></i>
        </div>
        <div className="pv-panel-widget-body">
          <div className="pv-panel-sub-title" style={{ marginBottom: '12.5px' }}>
            Verification Status
          </div>
          {renderStatusSteps()}
        </div>
      </div>

      {renderMessage()}

      {showCloseButton && (
        <div className="pv-panel-action-button" onClick={onClose}>
          Close
        </div>
      )}
    </div>
  )
}

export default PaymentVerificationPanel